import { api } from "../http";
import type { ZimbabweDistrict, ZimbabweProvinceLocations } from "../types";
import { readConfigEntry, saveConfigEntry } from "../../offline/db";
import { isRuntimeOfflineEnabled } from "../../offline/runtime";
import { isNavigatorOffline } from "../../offline/referenceCache";
import { getOfflineCachePolicy } from "../../offline/cachePolicy";
import { ZIMBABWE_PROVINCES_FALLBACK } from "../../constants/zimbabweLocationsFallback";

export const ZIMBABWE_LOCATIONS_CACHE_KEY = "locations/zimbabwe";

const loadCachedProvinces = async (): Promise<ZimbabweProvinceLocations[] | null> => {
  const entry = await readConfigEntry<ZimbabweProvinceLocations[]>(ZIMBABWE_LOCATIONS_CACHE_KEY);
  return entry?.data ?? null;
};

const persistProvinces = async (records: ZimbabweProvinceLocations[]) => {
  try {
    const ttlMs = getOfflineCachePolicy().locationTtlMs;
    await saveConfigEntry({
      type: ZIMBABWE_LOCATIONS_CACHE_KEY,
      data: records,
      updatedAt: Date.now(),
      expiresAt: ttlMs ? Date.now() + ttlMs : null,
    });
  } catch (error) {
    console.warn("Failed to cache Zimbabwe locations", error);
  }
};

const shouldUseCache = () => isNavigatorOffline() || isRuntimeOfflineEnabled();

const cloneFallback = (): ZimbabweProvinceLocations[] =>
  ZIMBABWE_PROVINCES_FALLBACK.map((province) => ({
    ...province,
    districts: province.districts.map((district) => ({ ...district })),
  }));

const matchProvince = (collection: ZimbabweProvinceLocations[], provinceId: string) =>
  collection.find((item) => item.id === provinceId || item.name === provinceId);

const provideFallback = async () => {
  const fallback = cloneFallback();
  await persistProvinces(fallback);
  return fallback;
};

export const zimbabweLocationsApi = {
  async list() {
    if (shouldUseCache()) {
      const cached = await loadCachedProvinces();
      if (cached) {
        return cached;
      }
    }
    try {
      const provinces = await api.get<ZimbabweProvinceLocations[]>("/api/reference/zimbabwe/provinces");
      const normalised = Array.isArray(provinces) && provinces.length ? provinces : cloneFallback();
      await persistProvinces(normalised);
      return normalised;
    } catch (error) {
      const cached = await loadCachedProvinces();
      if (cached) {
        return cached;
      }
      return provideFallback();
    }
  },
  async districts(provinceId: string) {
    if (shouldUseCache()) {
      const cached = await loadCachedProvinces();
      if (cached) {
        const province = matchProvince(cached, provinceId);
        if (province?.districts) {
          return province.districts;
        }
      }
    }
    try {
      const districts = await api.get<ZimbabweDistrict[]>(
        `/api/reference/zimbabwe/provinces/${encodeURIComponent(provinceId)}/districts`,
      );
      // ensure list cache includes these districts by refreshing cache
      try {
        const provinces = await api.get<ZimbabweProvinceLocations[]>("/api/reference/zimbabwe/provinces");
        await persistProvinces(provinces);
      } catch (refreshError) {
        console.warn("Failed to refresh province cache after districts fetch", refreshError);
      }
      if (!districts || !districts.length) {
        const fallbackProvince = matchProvince(cloneFallback(), provinceId);
        if (fallbackProvince?.districts?.length) {
          return fallbackProvince.districts;
        }
      }
      return districts;
    } catch (error) {
      const cached = await loadCachedProvinces();
      if (cached) {
        const province = matchProvince(cached, provinceId);
        if (province?.districts) {
          return province.districts;
        }
      }
      const fallbackProvince = matchProvince(cloneFallback(), provinceId);
      if (fallbackProvince?.districts) {
        return fallbackProvince.districts;
      }
      throw error;
    }
  },
};

export default zimbabweLocationsApi;
